import React, { useState, useEffect } from "react";
import type { AssetImage } from "gql/generated";
import {
  limitFill,
  fit,
} from "@cloudinary/transformation-builder-sdk/actions/resize";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";
import { css, cx } from "styled-system/css";
import { styled } from "styled-system/jsx";
import { CloudinaryImage, type CloudinaryImageProps } from "../CloudinaryImage";

import { shouldUseContainForImage } from "shared/utils/shouldUseContainForImage";
import { getImageThumbnailBackgroundColor } from "shared/utils/getImageBackgroundColor";
import { getBlurhashDataUrl } from "shared/utils";

type SmartImageShape = "circle" | "rounded" | "square";

export type SmartImageProps = {
  image?: Partial<AssetImage> | null;
  /** Container width in pixels (also used as the Cloudinary request width). */
  width: number;
  /** Container height in pixels. Defaults to width (square). */
  height?: number;
  /** Controls border-radius. Defaults to "rounded". */
  shape?: SmartImageShape;
  /** Force the transparency/contain path (e.g. for known logos). */
  isLogo?: boolean;
  previewVideo?: boolean;
  alt?: string;
  parts?: { image?: string; container?: string; frame?: string };
  /** Extra class applied to the outer container for layout overrides. */
  className?: string;
  videoSpecificProps?: CloudinaryImageProps["videoSpecificProps"];
  /**
   * When true, the container stretches to 100 % of its parent width.
   * The image covers up to `width` px; any remaining space shows the
   * blurhash as a background.
   */
  smartFill?: boolean;
  /**
   * When true, cover-mode images switch to contain on hover so the
   * user can preview the full uncropped image. The blurhash fills
   * any gaps revealed by the aspect-ratio change.
   */
  previewOnHover?: boolean;
};

const borderRadiusMap: Record<SmartImageShape, string> = {
  circle: "9999px",
  rounded: "var(--radii-100, 8px)",
  square: "0",
};

type DisplayMode =
  | "transparency"
  | "notCroppable"
  | "textSafety"
  | "portrait"
  | "cover";

const CONTAIN_PADDING_FRACTION = 0.08;
const TEXT_SAFETY_PADDING_FRACTION = 0.04;
const PREVIEW_RESIZE_FALLBACK_SCALE = 2;
const PREVIEW_RESIZE_MAX_SCALE = 2;

/**
 * Returns the server-computed transparency flag.
 *
 * For new images this is derived from Cloudinary's `image_metadata.ColorType`
 * (accurate format-level alpha detection). For older images it falls back to
 * the dominant-color palette check on the server side. Either way the server
 * is the source of truth — no client-side PNG heuristic needed.
 */
function isLikelyTransparent(
  image: Partial<AssetImage> | null | undefined,
): boolean {
  if (!image) return false;
  return image.hasTransparency === true;
}

/**
 * Returns true when the image's aspect ratio is close enough to the
 * container's that cover mode will show the image with minimal cropping.
 * Uses the "preserved content" metric: min(ratio)/max(ratio) gives the
 * fraction of the image that remains visible after cover-cropping.
 * A threshold of 0.75 means at most ~25 % of the image is clipped.
 */
function aspectRatioNearlyMatches(
  image: Partial<AssetImage> | null | undefined,
  containerW: number,
  containerH: number,
): boolean {
  const imgW = image?.width;
  const imgH = image?.height;
  if (!imgW || !imgH || !containerW || !containerH) return false;

  const imgRatio = imgW / imgH;
  const containerRatio = containerW / containerH;
  const preserved =
    Math.min(imgRatio, containerRatio) / Math.max(imgRatio, containerRatio);
  return preserved >= 0.75;
}

/**
 * Returns true when a portrait-oriented image is shown in a square or
 * landscape container. Cover mode would crop top/bottom aggressively,
 * so the image is better displayed fitted to the full height with
 * blurhash filling the sides.
 */
function isPortraitMismatch(
  image: Partial<AssetImage> | null | undefined,
  containerW: number,
  containerH: number,
): boolean {
  const imgW = image?.width;
  const imgH = image?.height;
  if (!imgW || !imgH) return false;
  if (!image?.detectedObjects?.hasFace) return false;
  return imgW < imgH && containerW >= containerH;
}

/**
 * Determines the display mode for an image based on its characteristics.
 *
 * Priority:
 *   1. transparency   — logo / alpha channel
 *   2. textSafety     — text near edges AND ratios nearly match (small pad)
 *   3. notCroppable   — text near edges AND ratios far apart (larger pad)
 *   4. cover          — aspect ratios close enough (< 25 % crop)
 *   5. portrait       — portrait image with face in square/landscape frame
 *   6. cover          — default
 */
function resolveDisplayMode(
  image: Partial<AssetImage> | null | undefined,
  containerW: number,
  containerH: number,
  isLogo?: boolean,
): DisplayMode {
  if (isLogo || isLikelyTransparent(image)) return "transparency";

  const textNearEdges = shouldUseContainForImage(
    image,
    isLogo,
    containerW,
    containerH,
  );

  if (textNearEdges) {
    return aspectRatioNearlyMatches(image, containerW, containerH)
      ? "textSafety"
      : "notCroppable";
  }

  if (aspectRatioNearlyMatches(image, containerW, containerH)) return "cover";

  if (isPortraitMismatch(image, containerW, containerH)) return "portrait";

  return "cover";
}

function getCoverSafeFitDimensions(
  image: Partial<AssetImage> | null | undefined,
  containerW: number,
  containerH: number,
): { width: number; height: number } {
  const imgW = image?.width;
  const imgH = image?.height;

  if (!imgW || !imgH || !containerW || !containerH) {
    return {
      width: Math.round(containerW * PREVIEW_RESIZE_FALLBACK_SCALE),
      height: Math.round(containerH * PREVIEW_RESIZE_FALLBACK_SCALE),
    };
  }

  const imgRatio = imgW / imgH;
  const containerRatio = containerW / containerH;

  const coverSafeDimensions =
    imgRatio > containerRatio
      ? {
          width: Math.round(containerH * imgRatio),
          height: containerH,
        }
      : {
          width: containerW,
          height: Math.round(containerW / imgRatio),
        };

  const maxW = containerW * PREVIEW_RESIZE_MAX_SCALE;
  const maxH = containerH * PREVIEW_RESIZE_MAX_SCALE;
  const scale = Math.min(
    maxW / coverSafeDimensions.width,
    maxH / coverSafeDimensions.height,
    1,
  );

  return {
    width: Math.round(coverSafeDimensions.width * scale),
    height: Math.round(coverSafeDimensions.height * scale),
  };
}

/**
 * SmartImage standardizes how user-uploaded images are displayed across
 * the Whereabouts app.
 *
 * It wraps CloudinaryImage and applies five display modes automatically:
 *   1. Transparent / logo images  -> contain + padding + dark bg (or none)
 *   2. Text-safety images         -> contain + narrow padding + blurhash bg
 *   3. Not-croppable images       -> contain + padding + blurhash bg
 *   4. Portrait images            -> full-height contain + blurhash on sides
 *   5. Default photos             -> cover + limitFill with g_auto
 *
 * The outer container IS the visual boundary -- it owns the background
 * color, border-radius, and overflow clipping.
 */
export const SmartImage = ({
  image,
  width,
  height: heightProp,
  shape = "rounded",
  isLogo,
  previewVideo,
  alt,
  parts,
  className,
  videoSpecificProps,
  smartFill,
  previewOnHover,
}: SmartImageProps) => {
  const h = heightProp ?? width;
  const mode = resolveDisplayMode(image, width, h, isLogo);
  const radius = borderRadiusMap[shape];
  const isContain = mode === "transparency" || mode === "notCroppable" || mode === "textSafety";
  const isPortrait = mode === "portrait";

  const padFraction =
    mode === "textSafety"
      ? TEXT_SAFETY_PADDING_FRACTION
      : CONTAIN_PADDING_FRACTION;

  const solidBg =
    mode === "transparency" ? getImageThumbnailBackgroundColor(image) : null;

  // Transparency overrides previewOnHover: transparent images always use
  // their normal contain + solid-bg path with no hover effect or blurhash.
  const previewActive = previewOnHover === true && mode !== "transparency";

  // Blurhash background for modes where the image doesn't fully cover the
  // container.
  const showContainerBlur =
    mode === "notCroppable" ||
    mode === "textSafety" ||
    isPortrait ||
    (smartFill === true && mode !== "transparency") ||
    previewActive;
  const imageBlurhash = image?.blurhash ?? "";
  const [blurBgUrl, setBlurBgUrl] = useState("");
  useEffect(() => {
    if (!showContainerBlur || !imageBlurhash) return;
    const url = getBlurhashDataUrl(imageBlurhash);
    if (url) setBlurBgUrl(url);
  }, [showContainerBlur, imageBlurhash]);

  // For padded contain modes, shrink the content area. Portrait mode uses
  // the full container dimensions (image extends top to bottom).
  const contentW = isContain
    ? Math.round(width * (1 - 2 * padFraction))
    : width;
  const contentH = isContain
    ? Math.round(h * (1 - 2 * padFraction))
    : h;
  const previewResizeDimensions = getCoverSafeFitDimensions(image, width, h);

  const getResize = () => {
    if (previewActive) {
      return fit()
        .width(previewResizeDimensions.width)
        .height(previewResizeDimensions.height);
    }
    if (smartFill || isPortrait) {
      return fit().width(width).height(h);
    }
    if (isContain) {
      return fit().width(contentW).height(contentH);
    }
    return limitFill().width(width).height(h).gravity(autoGravity());
  };

  const getResizeVideo = () => {
    if (smartFill || previewActive) {
      return fit().width(width).height(h);
    }
    return fit().width(contentW).height(contentH);
  };

  const containerStyle = css({
    overflow: "hidden",
    flexShrink: 0,
  });

  const [isHovered, setIsHovered] = useState(false);
  const showPreview = previewActive && isHovered;

  const coverSizeClass = css({ width: "100%", height: "100%" });
  const containSizeClass = css({ width: "auto", height: "auto" });

  const useContain = previewActive
    ? showPreview
    : smartFill || isContain || isPortrait;

  // Should the image element itself get rounded corners?
  // Portrait mode: image spans full height, no rounding.
  const roundImage =
    smartFill
    || showPreview
    || (isContain && mode !== "transparency");

  const imgBorderRadius = roundImage ? "8px" : undefined;

  // For previewActive, sizing & objectFit are controlled entirely via inline
  // style to avoid Panda CSS specificity issues. For other modes, the layout
  // class handles width/height and objectFit goes as a direct prop.
  const layoutClass = previewActive
    ? undefined
    : (smartFill || isContain || isPortrait)
      ? containSizeClass
      : coverSizeClass;

  const imageStyle: React.CSSProperties | undefined = smartFill
    ? {
        maxWidth: `${width}px`,
        maxHeight: `${h}px`,
        borderRadius: imgBorderRadius,
      }
    : previewActive
      ? {
          width: `${width}px`,
          height: `${h}px`,
          objectFit: useContain ? "contain" : "cover",
          borderRadius: imgBorderRadius,
        }
      : isContain
        ? {
            maxWidth: `${100 - 2 * padFraction * 100}%`,
            maxHeight: `${contentH}px`,
            borderRadius: imgBorderRadius,
          }
        : isPortrait
          ? {
              maxWidth: "100%",
              maxHeight: `${h}px`,
            }
          : undefined;

  const showDebug =
    typeof window !== "undefined" &&
    localStorage.getItem("smartImageDebug") === "true";

  return (
    <styled.div
      className={cx(containerStyle, className)}
      onMouseEnter={previewActive ? () => setIsHovered(true) : undefined}
      onMouseLeave={previewActive ? () => setIsHovered(false) : undefined}
      style={{
        cursor: previewActive ? "pointer" : undefined,
        width: smartFill ? "100%" : `${width}px`,
        maxWidth: "100%",
        height: `${h}px`,
        borderRadius: radius,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        ...(smartFill ? { flexShrink: 1 } : {}),
        ...(solidBg ? { backgroundColor: solidBg } : {}),
        ...(showContainerBlur && blurBgUrl
          ? {
              backgroundImage: blurBgUrl,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}),
      }}
    >
      {showDebug && (
        <styled.button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            const hasRgbaColor = image?.colors?.some(
              (c) => (c?.value ?? "").replace("#", "").length === 8,
            );
            const info = {
              assetId: image?._id ?? "(none)",
              mode,
              imageFormat: image?.format,
              "---transparency---": "",
              hasTransparency: image?.hasTransparency,
              hasRgbaColor,
              isLogo: !!isLogo,
              lightness: image?.lightness,
              "---detection---": "",
              hasText: image?.detectedObjects?.hasText,
              hasFace: image?.detectedObjects?.hasFace,
              qualityScore: image?.qualityScore,
              "---layout---": "",
              isContain,
              cloudinaryCrop: smartFill || previewActive
                ? "c_fit"
                : isContain
                  ? "c_fit"
                  : "c_lfill,g_auto",
              smartFill: !!smartFill,
              previewOnHover: !!previewOnHover,
              previewActive,
              showPreview,
              useContain,
              roundImage,
              imgBorderRadius,
              layoutClass: layoutClass ?? "(none)",
              imageStyle: JSON.stringify(imageStyle),
              "---dimensions---": "",
              imgW: image?.width,
              imgH: image?.height,
              containerW: width,
              containerH: h,
              shape,
              showContainerBlur,
              includeBgBlurHash: mode !== "transparency",
              colorCount: image?.colors?.length ?? 0,
            };
            const text = Object.entries(info)
              .map(([k, v]) => `${k}: ${v}`)
              .join("\n");
            alert(text + "\n\n— Click OK to copy to clipboard —");
            navigator.clipboard.writeText(text).catch(() => {});
          }}
          style={{
            position: "absolute",
            top: "4px",
            left: "4px",
            zIndex: 999,
            background: "rgba(0,0,0,0.7)",
            color: "#0f0",
            border: "1px solid #0f0",
            borderRadius: "4px",
            padding: "2px 6px",
            fontSize: "10px",
            cursor: "pointer",
            fontFamily: "monospace",
          }}
        >
          DBG
        </styled.button>
      )}
      <styled.div
        part={parts?.frame}
        style={{
          overflow: roundImage ? "hidden" : undefined,
          borderRadius: imgBorderRadius,
          width: "100%",
          height: "100%",
          ...(useContain
            ? { display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }
            : { display: "grid" }),
        }}
      >
        <CloudinaryImage
          image={image}
          imageStyleProps={{
            ...(layoutClass ? { className: layoutClass } : {}),
            aspectRatio: "unset",
            ...(previewActive
              ? {}
              : { objectFit: useContain ? ("contain" as const) : ("cover" as const) }),
          }}
          resize={getResize()}
          resizeVideo={getResizeVideo()}
          previewVideo={previewVideo}
          alt={alt}
          includeBgBlurHash={!showContainerBlur && mode !== "transparency"}
          solidBackgroundColor={mode === "transparency" ? solidBg : undefined}
          style={imageStyle}
          parts={parts}
          videoSpecificProps={videoSpecificProps}
        />
      </styled.div>
    </styled.div>
  );
};
